import isEmpty from 'lodash/isEmpty';

import { scriptStepTypes } from '@xmatters/common/constants/scriptSteps';
import { fetchScriptStep } from 'actions/flowDesigner/scriptSteps';

export const SCRIPT_STEP_MODAL_HIDE = 'SCRIPT_STEP_MODAL_HIDE';

export const SCRIPT_STEP_MODAL_NEW = 'SCRIPT_STEP_MODAL_NEW';
export const SCRIPT_STEP_MODAL_NEW_VERSION = 'SCRIPT_STEP_MODAL_NEW_VERSION';

export const SCRIPT_STEP_MODAL_LOAD = 'SCRIPT_STEP_MODAL_LOAD';
export const SCRIPT_STEP_MODAL_LOAD_START = 'SCRIPT_STEP_MODAL_LOAD_START';
export const SCRIPT_STEP_MODAL_LOAD_SUCCESS = 'SCRIPT_STEP_MODAL_LOAD_SUCCESS';
export const SCRIPT_STEP_MODAL_LOAD_FAIL = 'SCRIPT_STEP_MODAL_LOAD_FAIL';

export const SCRIPT_STEP_MODAL_SAVE = 'SCRIPT_STEP_MODAL_SAVE';
export const SCRIPT_STEP_MODAL_SAVE_START = `${SCRIPT_STEP_MODAL_SAVE}_START`;
export const SCRIPT_STEP_MODAL_SAVE_SUCCESS = `${SCRIPT_STEP_MODAL_SAVE}_SUCCESS`;
export const SCRIPT_STEP_MODAL_SAVE_FAIL = `${SCRIPT_STEP_MODAL_SAVE}_FAIL`;

export const SCRIPT_STEP_MODAL_DELETE = 'SCRIPT_STEP_MODAL_DELETE';
export const SCRIPT_STEP_MODAL_DELETE_START = 'SCRIPT_STEP_MODAL_DELETE_START';
export const SCRIPT_STEP_MODAL_DELETE_SUCCESS =
  'SCRIPT_STEP_MODAL_DELETE_SUCCESS';
export const SCRIPT_STEP_MODAL_DELETE_FAIL = 'SCRIPT_STEP_MODAL_DELETE_FAIL';
export const SCRIPT_STEP_MODAL_TOGGLE_ENDPOINT =
  'SCRIPT_STEP_MODAL_TOGGLE_ENDPOINT';
export const SCRIPT_STEP_VERSION_SELECTION_ALERT_DISMISSED =
  'SCRIPT_STEP_VERSION_SELECTION_ALERT_DISMISSED';

export const SCRIPT_STEP_MODAL_COPY_STEP = 'SCRIPT_STEP_MODAL_COPY_STEP';
export const SCRIPT_STEP_MODAL_SET_VERSION_EXPANDED =
  'SCRIPT_STEP_MODAL_SET_VERSION_EXPANDED';

export const hideModal = () => ({
  type: SCRIPT_STEP_MODAL_HIDE,
});

export const create = (commPlanUUID, { scriptStepType, dataOverrides }) => ({
  type: SCRIPT_STEP_MODAL_NEW,
  payload: {
    commPlanUUID,
    scriptStepType,
    dataOverrides,
  },
});

export const load = (stepId, selectedTab, isVersionsExpanded) => ({
  type: SCRIPT_STEP_MODAL_LOAD,
  meta: {
    xmWebApp: true,
    uri: `/webapi/xm/1/script-steps/${stepId}?embed=usage`,
    method: 'get',
  },
  payload: {
    selectedTab,
    isVersionsExpanded,
  },
});

// Clean up and transform the payload from the script step form to make it
// compatible with the xm-api.
const updatePayload = payload => {
  const transformedPayload = { ...payload };

  // Endpoint transformation
  if (!transformedPayload.includeEndpoint) {
    // No Endpoint; the endpoint information is cleared
    transformedPayload.endpointLabel = null;
    transformedPayload.endpointType = null;
  } else if (transformedPayload.endpointType === 'ANY_AUTH') {
    // ANY_AUTH is converted to no endpoint type for the xm-api
    transformedPayload.endpointType = null;
  }

  // Run location transformation
  if (transformedPayload.allowedEnvironment === 'BOTH') {
    // BOTH is converted to ANY for the xm-api
    transformedPayload.allowedEnvironment = 'ANY';
  }

  const { callableFlowCaller, callableFlowReceiver, id } = payload;
  if (isEmpty(callableFlowCaller) || callableFlowCaller.id === id) {
    delete transformedPayload.callableFlowCaller;
  }

  if (callableFlowReceiver && typeof callableFlowReceiver === 'string') {
    transformedPayload.callableFlowReceiver = {
      id: payload.callableFlowReceiver,
    };
  } else if (isEmpty(callableFlowReceiver) || callableFlowReceiver.id === id) {
    delete transformedPayload.callableFlowReceiver;
  }

  return transformedPayload;
};

export const save = payload => (dispatch, getState) => {
  const { id, scriptStepType, callableFlowReceiver } = payload;
  const state = getState();
  const params = updatePayload(payload);

  if (
    scriptStepType === scriptStepTypes.CALLABLE_FLOW_CALLER &&
    id &&
    !callableFlowReceiver
  ) {
    const receiverStep = state.flowDesigner.customSteps?.find(
      step => step.callableFlowCaller?.id === id,
    );

    if (receiverStep) {
      params.callableFlowReceiver = { id: receiverStep.id };
    }
  }

  const savePromise = dispatch({
    type: SCRIPT_STEP_MODAL_SAVE,
    meta: {
      xmWebApp: true,
      uri: '/webapi/xm/1/script-steps',
      method: 'post',
      params,
    },
  });

  return params.callableFlowReceiver
    ? savePromise.then(() =>
        dispatch(fetchScriptStep(params.callableFlowReceiver.id)),
      )
    : savePromise;
};

const removeStep = stepId => ({
  type: SCRIPT_STEP_MODAL_DELETE,
  meta: {
    xmWebApp: true,
    uri: `/webapi/xm/1/script-steps/${stepId}`,
    method: 'del',
  },
});

export const remove = (stepId, next) => dispatch =>
  Promise.all([dispatch(removeStep(stepId))]).then(() => {
    if (next) {
      dispatch(load(next.id));
    }
  });

export const toggleEndpoint = () => ({
  type: SCRIPT_STEP_MODAL_TOGGLE_ENDPOINT,
});

export const createVersion = (baseStep, copyString, commPlanUUID) => ({
  type: SCRIPT_STEP_MODAL_NEW_VERSION,
  baseStep,
  copyString,
  commPlanUUID,
});

export const setVersionsExpanded = isVersionsExpanded => ({
  type: SCRIPT_STEP_MODAL_SET_VERSION_EXPANDED,
  payload: {
    isVersionsExpanded,
  },
});

export const versionSelectionAlertDismissed = () => ({
  type: SCRIPT_STEP_VERSION_SELECTION_ALERT_DISMISSED,
});

export const copyStep = (baseStep, namePrefix, commPlanUUID) => ({
  type: SCRIPT_STEP_MODAL_COPY_STEP,
  baseStep,
  namePrefix,
  commPlanUUID,
});
